// The unit's inbox: active course orders from OPD, each bookable into slots.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import DaycareScreen from "../components/DaycareScreen";
import BookCourseDialog from "../components/BookCourseDialog";
import ToneChip from "../components/ToneChip";
import { fetchOrders, fetchUnits } from "../api/daycareApi";
import { groupLabel } from "../utils/sessionView";
import { formatIstDate } from "../../../utils/istDateTime";
import { IPD_COLORS, IpdButton, IpdSectionCard, IpdTextField } from "../../ipd/utils";
import { END_MODE, GROUP_CLEAN, ORDER_STATUS, WEEKDAY_LABELS } from "../constants";

const HEAD_SX = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: IPD_COLORS.textSecondary,
  bgcolor: IPD_COLORS.panelHeaderBg,
  whiteSpace: "nowrap",
};
const CELL_SX = { fontSize: 13, color: IPD_COLORS.textPrimary, verticalAlign: "top" };
const SUB_SX = { fontSize: 12, color: IPD_COLORS.textSecondary };

function scheduleOf(order) {
  const days = order.repeat_days || [];
  if (!days.length) return "Single session";
  return days.map((d) => WEEKDAY_LABELS[d]).join(", ");
}

function lengthOf(order) {
  if (!(order.repeat_days || []).length) return formatIstDate(order.first_date);
  return order.end_mode === END_MODE.DATE ? `Until ${formatIstDate(order.end_date)}` : `${order.session_count} sessions`;
}

export default function DaycareOrders() {
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [orders, setOrders] = useState([]);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchUnits()
      .then((list) => {
        setUnits(list || []);
        if (list?.length) setUnitId(list[0].id);
        else setLoading(false);
      })
      .catch((err) => {
        setError(err?.body?.message || "Could not load the day care units");
        setLoading(false);
      });
  }, []);

  // Only the first load blanks the screen; a refresh after booking keeps it.
  const load = useCallback(async (id) => {
    if (!id) return;
    setError("");
    try {
      setOrders(await fetchOrders({ unit_id: id, status: ORDER_STATUS.ACTIVE }));
    } catch (err) {
      setError(err?.body?.message || "Could not load the orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(unitId); }, [load, unitId]);

  const unit = units.find((u) => u.id === unitId);

  return (
    <DaycareScreen
      title="Course orders"
      description="Courses ordered from OPD. Book each course's sessions into a shift and machine."
      badge={orders.length}
      loading={loading}
      error={error}
      onRetry={() => load(unitId)}
      actions={units.length > 1 && (
        <IpdTextField select value={unitId} onChange={(e) => setUnitId(e.target.value)} sx={{ minWidth: 200 }}>
          {units.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
        </IpdTextField>
      )}
    >
      <IpdSectionCard title={unit ? unit.name : "No day care unit"} subtitle="Active courses" noPadding>
        {orders.length === 0 ? (
          <Typography sx={{ ...SUB_SX, p: 3, textAlign: "center" }}>No active course orders.</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_SX}>Patient</TableCell>
                  <TableCell sx={HEAD_SX}>Course</TableCell>
                  <TableCell sx={HEAD_SX}>Days</TableCell>
                  <TableCell sx={HEAD_SX}>Machines</TableCell>
                  <TableCell sx={HEAD_SX}>Progress</TableCell>
                  <TableCell sx={HEAD_SX}>Ordered by</TableCell>
                  <TableCell sx={HEAD_SX} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} hover>
                    <TableCell sx={CELL_SX}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{o.patient_name || o.patient_id}</Typography>
                      <Typography sx={SUB_SX}>{o.patient_id}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...CELL_SX, maxWidth: 360 }}>
                      <Typography sx={{ fontSize: 13 }}>{o.service_name}</Typography>
                      {o.instructions && <Typography sx={SUB_SX}>{o.instructions}</Typography>}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <Typography sx={{ fontSize: 13 }}>{scheduleOf(o)}</Typography>
                      <Typography sx={SUB_SX}>{`${lengthOf(o)} · from ${formatIstDate(o.first_date)}`}</Typography>
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <ToneChip tone={o.patient_group === GROUP_CLEAN ? "neutral" : "bad"} label={groupLabel(o.patient_group)} />
                      {o.retest_due && <Typography sx={{ ...SUB_SX, color: IPD_COLORS.warning.text, mt: 0.5 }}>Markers retest due</Typography>}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <Typography sx={{ fontSize: 13 }}>{`${o.sessions_admitted} done`}</Typography>
                      <Typography sx={SUB_SX}>
                        {`${o.sessions_booked} booked${o.sessions_missed ? ` · ${o.sessions_missed} missed` : ""}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={CELL_SX}>{o.ordered_by_name || "—"}</TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      <IpdButton size="small" onClick={() => setBooking(o)}>Book sessions</IpdButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </IpdSectionCard>
      {booking && (
        <BookCourseDialog
          order={booking}
          unit={unit}
          onClose={() => setBooking(null)}
          onBooked={() => load(unitId)}
        />
      )}
    </DaycareScreen>
  );
}
